import { Flex, Grid, Text } from 'theme-ui';
import ProductOptionItem from './OptionItem';
import { Dispatch, SetStateAction } from 'react';
import SizeChartButton from '~/components/Generic/PDPWrapper/SizeChartButton';
import { VariantWithStock } from '~/hooks/components/use-product-availability';

type Props = {
  type: string;
  options: Queries.OptionValuesFragmentFragment[];
  selectedOptions: Queries.VariantDetailsFragmentFragment['optionValues'];
  handleSelection: (option: Queries.OptionValuesFragmentFragment) => void;
  isKit: boolean;
  presentation: string | null | undefined;
  setShowSizeChart?: Dispatch<SetStateAction<boolean>>;
  description?: string | null | undefined;
  productId: string;
  variants: VariantWithStock[];
};

const ProductOptionSelector = ({
  type,
  options,
  selectedOptions,
  handleSelection,
  isKit,
  presentation,
  setShowSizeChart,
  description,
  productId,
  variants,
}: Props) => {
  // Check if a variant with given option is available based on current selection
  const isOptionAvailable = (option: Queries.OptionValuesFragmentFragment) => {
    // Find the currently selected option of the other type (size if checking color, color if checking size)
    const otherTypeSelection = selectedOptions?.find((so) => so?.option_type?.[0]?.slug !== type);

    // If no other selection yet, check if any variant with this option is available
    if (!otherTypeSelection) {
      return variants.some(
        (variant) =>
          variant.inStock &&
          !variant.comingSoon &&
          !variant.discontinued &&
          variant.optionValues?.some((ov) => ov?.slug === option.slug)
      );
    }

    // Check if any variant with both this option and the other selected option is available
    return variants.some(
      (variant) =>
        variant.inStock &&
        !variant.comingSoon &&
        !variant.discontinued &&
        variant.optionValues?.some((ov) => ov?.slug === option.slug) &&
        variant.optionValues?.some((ov) => ov?.slug === otherTypeSelection.slug)
    );
  };

  return (
    <fieldset sx={{ p: 0, mx: 0, border: 0 }}>
      <Grid
        sx={{
          display: 'grid',
          gap: [isKit ? '1.6rem' : '0.8rem', '1.6rem'],
          position: 'relative',
        }}
      >
        <legend
          sx={{
            fontSize: ['1.5rem', '1.7rem'],
            lineHeight: 1,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            letterSpacing: '-0.02em',
            p: 0,
          }}
        >
          <span sx={{ fontWeight: 500 }}>{`${type}: `}</span>
          <span sx={{ fontWeight: 400 }}>{`${presentation}`}</span>
        </legend>
        <Flex
          sx={{
            width: '100%',
            alignItems: 'center',
            columnGap: isKit ? '1.2rem' : '1.6rem',
            rowGap: '0.8rem',
            flexWrap: 'wrap',
          }}
        >
          {options.map((option) => {
            return (
              <ProductOptionItem
                option={option}
                type={type}
                selectedOptions={selectedOptions}
                handleSelection={handleSelection}
                key={option.slug}
                isKit={isKit}
                productId={productId}
                isOptionAvailable={isOptionAvailable(option)}
              />
            );
          })}
        </Flex>

        {!isKit && (
          <Flex
            sx={{
              borderTop: '1px solid',
              borderTopColor: 'cloud',
              pt: '1.2rem',
              mt: ['1.6rem', 0],
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '1.6rem',
            }}
          >
            <Text
              as="p"
              sx={{
                fontSize: ['1.2rem', '1.4rem'],
                lineHeight: 1,
                letterSpacing: '-0.01em',
                color: 'UIGray2',
                fontWeight: 400,
              }}
            >
              {description}
            </Text>
            {setShowSizeChart ? <SizeChartButton setShowSizeChart={setShowSizeChart} /> : <div />}
          </Flex>
        )}
      </Grid>
    </fieldset>
  );
};

export default ProductOptionSelector;
